import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ShieldAlert } from "lucide-react";

const ORG_TYPES = [
  { value: "business", label: "Local Business" },
  { value: "church", label: "Church / Faith Org" },
  { value: "nonprofit", label: "Nonprofit" },
  { value: "city", label: "City / Municipal" },
  { value: "county", label: "County Government" },
  { value: "school-district", label: "School District" },
];

export default function Signup() {
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "employer" ? "employer" : "youth");
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    date_of_birth: "", age: "",
    gender: "",
    company_name: "", organization_type: "business",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, startGoogle } = useAuth();
  const nav = useNavigate();

  const computedAge = form.date_of_birth ? Math.max(0, Math.floor((Date.now() - new Date(form.date_of_birth).getTime()) / (365.25 * 24 * 3600 * 1000))) : null;
  const isMinor = role === "youth" && computedAge !== null && computedAge < 18;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (role === "youth") {
      if (!form.date_of_birth) return toast.error("Please enter your date of birth");
      if (computedAge < 13 || computedAge > 21) return toast.error("NextHire is for ages 13–21");
      if (isMinor && !consent) return toast.error("Please confirm parental/guardian consent to continue.");
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, password: form.password, role,
        date_of_birth: role === "youth" ? form.date_of_birth : undefined,
        age: role === "youth" ? computedAge : undefined,
        gender: role === "youth" && form.gender ? form.gender : undefined,
        company_name: role === "employer" ? form.company_name : undefined,
        organization_type: role === "employer" ? form.organization_type : undefined,
      };
      const u = await register(payload);
      toast.success("Welcome to NextHire!");
      if (u.role === "employer") nav("/employer");
      else nav("/dashboard");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="nh-container py-14 grid md:grid-cols-2 gap-10 items-start">
      <Toaster position="top-center"/>
      <div className="hidden md:block">
        <img src={LOGO_URL} alt="NextHire" className="h-24 w-24 rounded-xl border-2 border-black object-cover"/>
        <h1 className="font-display font-black text-5xl mt-6">Create your <span className="bg-nh-accent px-2 border-2 border-black rounded-md">free</span> account.</h1>
        <p className="text-nh-muted mt-4 max-w-md">Youth 13–21, or a business, nonprofit, church, school district, city or county ready to hire — free forever.</p>
      </div>
      <div className="nh-card p-8">
        <div className="flex gap-2 mb-5">
          <button type="button" onClick={()=>setRole("youth")} className={`nh-btn text-xs flex-1 ${role==="youth" ? "nh-btn-primary" : "nh-btn-ghost"}`} data-testid="role-youth">I'm a Youth (13–21)</button>
          <button type="button" onClick={()=>setRole("employer")} className={`nh-btn text-xs flex-1 ${role==="employer" ? "nh-btn-primary" : "nh-btn-ghost"}`} data-testid="role-employer">I'm Hiring</button>
        </div>

        <button onClick={()=>startGoogle(role)} className="nh-btn nh-btn-ghost w-full" data-testid="signup-google">
          Continue with Google as {role === "youth" ? "Youth" : "Employer"}
        </button>
        <div className="my-4 flex items-center gap-3 text-xs uppercase text-nh-muted">
          <div className="flex-1 h-px bg-black/20"/>or<div className="flex-1 h-px bg-black/20"/>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase">Full Name</label>
            <input required value={form.name} onChange={set("name")} className="nh-input mt-1" data-testid="signup-name"/>
          </div>
          <div>
            <label className="text-xs font-bold uppercase">Email</label>
            <input required type="email" value={form.email} onChange={set("email")} className="nh-input mt-1" data-testid="signup-email"/>
          </div>
          <div>
            <label className="text-xs font-bold uppercase">Password (6+ chars)</label>
            <input required type="password" minLength={6} value={form.password} onChange={set("password")} className="nh-input mt-1" data-testid="signup-password"/>
          </div>
          {role === "youth" ? (
            <>
              <div>
                <label className="text-xs font-bold uppercase">Date of Birth</label>
                <input required type="date" value={form.date_of_birth} onChange={set("date_of_birth")} className="nh-input mt-1" data-testid="signup-dob"/>
                {computedAge !== null && (
                  <p className="text-xs text-nh-muted mt-1">You are <b>{computedAge}</b>. We'll auto-filter jobs to what's legal for your age.</p>
                )}
              </div>
              <div>
                <label className="text-xs font-bold uppercase">Sex (optional)</label>
                <select value={form.gender} onChange={set("gender")} className="nh-input mt-1" data-testid="signup-gender">
                  <option value="">Prefer not to say</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-xs font-bold uppercase">Organization Type</label>
                <select value={form.organization_type} onChange={set("organization_type")} className="nh-input mt-1" data-testid="signup-org-type">
                  {ORG_TYPES.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase">Organization Name</label>
                <input required value={form.company_name} onChange={set("company_name")} className="nh-input mt-1" data-testid="signup-company"/>
              </div>
            </>
          )}

          {isMinor && (
            <label className="flex items-start gap-2 nh-card bg-nh-accent p-3 shadow-none">
              <input type="checkbox" checked={consent} onChange={(e)=>setConsent(e.target.checked)} className="mt-1" data-testid="signup-consent"/>
              <span className="text-xs">
                <ShieldAlert size={14} className="inline mr-1"/>I confirm that my parent or legal guardian has read the safety terms and consents to my use of NextHire.
              </span>
            </label>
          )}

          <button disabled={loading} className="nh-btn nh-btn-primary w-full" data-testid="signup-submit">
            {loading ? "Creating account…" : "Create Account"}
          </button>
        </form>

        <p className="text-sm mt-5">
          Already registered? <Link to="/login" className="font-bold underline" data-testid="link-login">Log in</Link>
        </p>
      </div>
    </div>
  );
}
